import jsPDF from "jspdf";
import QRCode from "qrcode";

/**
 * Generate QR code as data URL
 */
const generateQRCode = async (data, size = 100) => {
  try {
    return await QRCode.toDataURL(data, {
      width: size,
      margin: 1,
      errorCorrectionLevel: 'M'
    });
  } catch (err) {
    console.error('QR code generation failed:', err);
    return null;
  }
};

/**
 * Format price for display on label
 */
const formatPrice = (price) => {
  if (!price && price !== 0) return '';
  return Number(price).toFixed(2).replace('.', ',');
};

/**
 * Export product labels to PDF with QR codes, grouped by brand
 * Each label contains: brand, product name, selling price, and QR code with PRD:{product_id}
 * Labels are 17mm high x 50mm wide, designed for shelf stickers.
 * Labels share borders (no gaps) for easier cutting.
 *
 * @param {Array} products - Array of product objects with id, product_name, brand, selling_price_incl_vat
 * @param {Object} options - Export options
 * @param {string} options.locationName - Optional location name for subtitle
 */
export const exportProductLabelsToPDF = async (products, options = {}) => {
  const { locationName } = options;

  if (!products || products.length === 0) {
    throw new Error('Geen producten gevonden om te exporteren');
  }

  // Group products by brand, then sort within each group
  const groupedByBrand = {};
  products.forEach(p => {
    const brand = p.brand || 'Zonder merk';
    if (!groupedByBrand[brand]) {
      groupedByBrand[brand] = [];
    }
    groupedByBrand[brand].push(p);
  });

  // Sort brands alphabetically, with "Zonder merk" at the end
  const sortedBrands = Object.keys(groupedByBrand).sort((a, b) => {
    if (a === 'Zonder merk') return 1;
    if (b === 'Zonder merk') return -1;
    return a.localeCompare(b);
  });

  // Sort products within each brand
  sortedBrands.forEach(brand => {
    groupedByBrand[brand].sort((a, b) =>
      (a.product_name || a.name || '').localeCompare(b.product_name || b.name || '')
    );
  });

  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.width;
  const pageHeight = doc.internal.pageSize.height;

  // Label dimensions in mm - no gaps between labels
  const labelWidth = 50;
  const labelHeight = 17;
  const qrSize = 15;
  const marginLeft = 8;
  const marginTop = 15;
  const marginBottom = 10;
  const brandHeaderHeight = 6; // Height for brand title row

  // Calculate grid
  const cols = Math.floor((pageWidth - marginLeft * 2) / labelWidth);

  // Pre-generate all QR codes
  const allProducts = sortedBrands.flatMap(brand => groupedByBrand[brand]);
  const qrCodesMap = {};
  const qrCodes = await Promise.all(
    allProducts.map(p => generateQRCode(`PRD:${p.id}`, 150))
  );
  allProducts.forEach((p, i) => {
    qrCodesMap[p.id] = qrCodes[i];
  });

  // Title on first page
  const drawPageHeader = (pageNum) => {
    doc.setFontSize(12);
    doc.setFont(undefined, "bold");
    const title = locationName
      ? `Productstickers - ${locationName}`
      : "Productstickers";
    doc.text(title, pageWidth / 2, 8, { align: 'center' });

    doc.setFontSize(8);
    doc.setFont(undefined, "normal");
    doc.setTextColor(100, 100, 100);
    const subtitle = pageNum > 1
      ? `${allProducts.length} producten - pagina ${pageNum}`
      : `${allProducts.length} producten`;
    doc.text(subtitle, pageWidth / 2, 12, { align: 'center' });
    doc.setTextColor(0, 0, 0);
  };

  drawPageHeader(1);

  let currentY = marginTop;
  let currentCol = 0;
  let pageNum = 1;

  /**
   * Draw a single product label at position (x, y)
   */
  const drawLabel = (product, x, y) => {
    const qrDataUrl = qrCodesMap[product.id];

    // Label border (dashed cutting guide) - shared borders
    doc.setDrawColor(180, 180, 180);
    doc.setLineDashPattern([1, 1], 0);
    doc.rect(x, y, labelWidth, labelHeight);
    doc.setLineDashPattern([], 0);
    doc.setDrawColor(0, 0, 0);

    // Layout: left side = text, right side = QR code
    const textAreaWidth = labelWidth - qrSize - 3;
    const textX = x + 1.5;
    const qrX = x + labelWidth - qrSize - 1;
    const qrY = y + (labelHeight - qrSize) / 2;

    // QR code on the right
    if (qrDataUrl) {
      doc.addImage(qrDataUrl, 'PNG', qrX, qrY, qrSize, qrSize);
    }

    // Brand (top left, small, gray)
    const brandName = product.brand || '';
    if (brandName) {
      doc.setFontSize(4.5);
      doc.setFont(undefined, "normal");
      doc.setTextColor(120, 120, 120);
      const brandTruncated = doc.getTextWidth(brandName) > textAreaWidth
        ? brandName.slice(0, Math.floor(textAreaWidth / doc.getTextWidth('M'))) + '...'
        : brandName;
      doc.text(brandTruncated, textX, y + 3);
      doc.setTextColor(0, 0, 0);
    }

    // Product name (below brand, up to 2 lines) - slightly larger font
    doc.setFontSize(6);
    doc.setFont(undefined, "bold");
    const productName = product.product_name || product.name || '';
    const nameLines = doc.splitTextToSize(productName, textAreaWidth);
    const displayLines = nameLines.slice(0, 2);
    if (nameLines.length > 2) {
      const lastLine = displayLines[1];
      displayLines[1] = lastLine.length > 3
        ? lastLine.slice(0, -1) + '...'
        : lastLine;
    }
    const nameY = brandName ? y + 6 : y + 4.5;
    doc.text(displayLines, textX, nameY);

    // Price (below name, large font)
    const price = product.selling_price_incl_vat;
    if (price) {
      doc.setFontSize(13);
      doc.setFont(undefined, "bold");
      const priceText = `€${formatPrice(price)}`;
      const priceY = brandName
        ? (displayLines.length > 1 ? y + 15 : y + 13)
        : (displayLines.length > 1 ? y + 13.5 : y + 11.5);
      doc.text(priceText, textX, priceY);
    }
  };

  /**
   * Check if we need a new page and add one if so
   */
  const ensureSpace = (heightNeeded) => {
    if (currentY + heightNeeded > pageHeight - marginBottom) {
      doc.addPage();
      pageNum++;
      drawPageHeader(pageNum);
      currentY = marginTop;
      currentCol = 0;
      return true;
    }
    return false;
  };

  // Draw labels grouped by brand
  for (const brand of sortedBrands) {
    const brandProducts = groupedByBrand[brand];

    // Check if we need space for brand header + at least one row of labels
    ensureSpace(brandHeaderHeight + labelHeight);

    // If we're not at the start of a row, move to next row for brand header
    if (currentCol > 0) {
      currentY += labelHeight;
      currentCol = 0;
      ensureSpace(brandHeaderHeight + labelHeight);
    }

    // Draw brand header
    doc.setFontSize(9);
    doc.setFont(undefined, "bold");
    doc.setTextColor(50, 50, 50);
    doc.text(brand, marginLeft, currentY + 4);
    doc.setTextColor(0, 0, 0);

    // Draw a subtle line under the brand name
    doc.setDrawColor(200, 200, 200);
    doc.setLineWidth(0.2);
    doc.line(marginLeft, currentY + 5, pageWidth - marginLeft, currentY + 5);
    doc.setDrawColor(0, 0, 0);
    doc.setLineWidth(0.1);

    currentY += brandHeaderHeight;

    // Draw labels for this brand
    for (const product of brandProducts) {
      // Check if we need a new page
      if (currentCol === 0) {
        ensureSpace(labelHeight);
      }

      const x = marginLeft + currentCol * labelWidth;
      drawLabel(product, x, currentY);

      currentCol++;
      if (currentCol >= cols) {
        currentCol = 0;
        currentY += labelHeight;
      }
    }

    // Move to next row after brand group (if not already at start of row)
    if (currentCol > 0) {
      currentY += labelHeight;
      currentCol = 0;
    }

    // Add small gap after each brand group
    currentY += 2;
  }

  // Save
  const filename = locationName
    ? `productstickers_${locationName.toLowerCase().replace(/\s+/g, '_')}.pdf`
    : "productstickers.pdf";
  doc.save(filename);
};

/**
 * Export a single product label to PDF
 * Useful for printing just one product's label from the product detail page
 *
 * @param {Object} product - Product object with id, product_name or name, brand, selling_price_incl_vat
 */
export const exportSingleProductLabelToPDF = async (product) => {
  if (!product) {
    throw new Error('Geen product opgegeven');
  }

  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.width;

  // Label dimensions
  const labelWidth = 50;
  const labelHeight = 17;
  const qrSize = 15;

  // Center the label on the page
  const x = (pageWidth - labelWidth) / 2;
  const y = 30;

  // Title
  doc.setFontSize(10);
  doc.setFont(undefined, "bold");
  doc.text("Productsticker", pageWidth / 2, 15, { align: 'center' });

  doc.setFontSize(8);
  doc.setFont(undefined, "normal");
  doc.setTextColor(100, 100, 100);
  const productName = product.product_name || product.name || '';
  doc.text(productName, pageWidth / 2, 21, { align: 'center' });
  doc.setTextColor(0, 0, 0);

  // Generate QR code
  const qrDataUrl = await generateQRCode(`PRD:${product.id}`, 150);

  // Draw label border
  doc.setDrawColor(180, 180, 180);
  doc.setLineDashPattern([1, 1], 0);
  doc.rect(x, y, labelWidth, labelHeight);
  doc.setLineDashPattern([], 0);
  doc.setDrawColor(0, 0, 0);

  // Layout
  const textAreaWidth = labelWidth - qrSize - 3;
  const textX = x + 1.5;
  const qrX = x + labelWidth - qrSize - 1;
  const qrY = y + (labelHeight - qrSize) / 2;

  // QR code
  if (qrDataUrl) {
    doc.addImage(qrDataUrl, 'PNG', qrX, qrY, qrSize, qrSize);
  }

  // Brand
  const brandName = product.brand || '';
  if (brandName) {
    doc.setFontSize(4.5);
    doc.setFont(undefined, "normal");
    doc.setTextColor(120, 120, 120);
    const brandTruncated = doc.getTextWidth(brandName) > textAreaWidth
      ? brandName.slice(0, Math.floor(textAreaWidth / doc.getTextWidth('M'))) + '...'
      : brandName;
    doc.text(brandTruncated, textX, y + 3);
    doc.setTextColor(0, 0, 0);
  }

  // Product name
  doc.setFontSize(6);
  doc.setFont(undefined, "bold");
  const nameLines = doc.splitTextToSize(productName, textAreaWidth);
  const displayLines = nameLines.slice(0, 2);
  if (nameLines.length > 2) {
    const lastLine = displayLines[1];
    displayLines[1] = lastLine.length > 3
      ? lastLine.slice(0, -1) + '...'
      : lastLine;
  }
  const nameY = brandName ? y + 6 : y + 4.5;
  doc.text(displayLines, textX, nameY);

  // Price
  const price = product.selling_price_incl_vat;
  if (price) {
    doc.setFontSize(13);
    doc.setFont(undefined, "bold");
    const priceText = `€${formatPrice(price)}`;
    const priceY = brandName
      ? (displayLines.length > 1 ? y + 15 : y + 13)
      : (displayLines.length > 1 ? y + 13.5 : y + 11.5);
    doc.text(priceText, textX, priceY);
  }

  // Save
  const safeName = productName.toLowerCase().replace(/[^a-z0-9]/g, '_').slice(0, 30);
  doc.save(`productsticker_${safeName}.pdf`);
};
